// ── Slicer bindings ──────────────────────────────────────────────
// A governed widget stores a POINTER to its controller (`dateControllerId` /
// `subjectControllerId` on its builder misc), never a copy of the controller's values.
// This module is the single place that knows WHERE those pointers live per widget type,
// and what counts as an *effective* binding. It stays free of React and of the widget
// registry so `buildQueries`, the dependency index, the Control Panel and the import
// remap can all share it.

import { declinesSessionWindow, usesSessionWindow } from "../AddWidgetModal/constants";
import { resolveTemplate, type SlicerValues } from "../Utils/resolveTemplate";
import { assignKeys } from "../Utils/slicerKeys.mjs";
import type {
  ComponentSpec,
  ControllerBindingFields,
  ControllerKind,
  DynamicControllerBuilderMisc,
  DynamicControllerComponentSpec,
  SubjectEntity,
} from "./types";
import type { GridWidgetType } from "../types";

// The set of slicer keys a title or text string quotes, so the dependency index, the
// delete guard and the hover highlight read tokens the same way the resolver does.
export { slicerKeysInTemplate } from "../Utils/resolveTemplate";

/** Every filter section a controller can govern, in display order. */
export const CONTROLLER_KINDS: readonly ControllerKind[] =
  ["date", "single_date", "subject", "single_player"];

/**
 * The kinds the configurator offers — a controller holds exactly ONE of them.
 * `single_player` and `single_date` are legacy: a single-player binding now follows a
 * `subject` controller's first athlete, and a single-date binding follows a `date`
 * controller's range end. No NEW controller gets a legacy kind, but saved controllers
 * that carry one keep working until the coach removes it.
 */
export const SELECTABLE_CONTROLLER_KINDS: readonly ControllerKind[] = ["date", "subject"];

/** Whether a controller with these controls can satisfy a binding of this kind. */
export function controllerOffersKind(
  controls: readonly ControllerKind[] | undefined,
  kind: ControllerKind,
): boolean {
  if (!controls) return false;
  if (controls.includes(kind)) return true;
  // A subject controller also feeds single-player bindings (first athlete in its picker,
  // drills discarded), and a date controller feeds single-date bindings (its range end).
  if (kind === "single_player") return controls.includes("subject");
  return kind === "single_date" && controls.includes("date");
}

/**
 * The athlete a single-player binding resolves to, as a `player-<id>` string ("" when the
 * controller names none). Mirrors the engine: the legacy single-player picker wins while
 * the controller still controls it; otherwise the first athlete in the subject picker.
 */
export function controllerAthlete(
  controls: readonly ControllerKind[] | undefined,
  misc: { selectedPlayer?: string; selectedSubjects?: string[] } | undefined,
): string {
  if (controls?.includes("single_player")) return misc?.selectedPlayer ?? "";
  return (misc?.selectedSubjects ?? []).find((s) => s.startsWith("player-")) ?? "";
}

/** The single-date picker's field shape — what a bound single-date section displays. */
export interface ControllerSingleDate {
  dateMode: "relative" | "absolute";
  dateOffset: number;
  fixedDate: string;
}

/**
 * The one day a single-date binding resolves to. Mirrors the engine: the legacy
 * single-date picker wins while the controller still controls it; otherwise the END of
 * the date range — without the custom range, every preset ends today.
 */
export function controllerSingleDate(
  controls: readonly ControllerKind[] | undefined,
  misc: {
    dateMode?: "relative" | "absolute";
    dateOffset?: number;
    fixedDate?: string;
    showAdvancedDate?: boolean;
    endDateType?: string;
    endDateAbsolute?: string;
    endDateOffset?: number | null;
  } | undefined,
): ControllerSingleDate {
  if (controls?.includes("single_date")) {
    return {
      dateMode: misc?.dateMode ?? "relative",
      dateOffset: misc?.dateOffset ?? 0,
      fixedDate: misc?.fixedDate ?? "",
    };
  }
  if (misc?.showAdvancedDate) {
    if (misc.endDateType === "absolute") {
      return { dateMode: "absolute", dateOffset: 0, fixedDate: misc.endDateAbsolute ?? "" };
    }
    return { dateMode: "relative", dateOffset: misc.endDateOffset ?? 0, fixedDate: "" };
  }
  return { dateMode: "relative", dateOffset: 0, fixedDate: "" };
}

/** Runtime widget ids carry a `<type>-` prefix; builder misc stores the bare UUID. */
export const CONTROLLER_TYPE = "slicer";

/** Palette a new slicer's slice_color is drawn from — the first color not already in use. */
export const SLICE_COLOR_PALETTE = [
  "#fa8c16", "#1677ff", "#52c41a", "#722ed1", "#eb2f96", "#13c2c2", "#faad14", "#2f54eb",
] as const;

export const DEFAULT_SLICE_COLOR = SLICE_COLOR_PALETTE[0];

/** A slicer's highlight color — the followers' hover outline and corner badge disc. */
export function sliceColor(misc: { slice_color?: string } | undefined): string {
  return misc?.slice_color || DEFAULT_SLICE_COLOR;
}

/**
 * slice_color washed 70% toward whitish-gray — the slicer tile's own faint border
 * tint, loud enough to match tile and badge, quiet enough to stay chrome.
 */
export function faintSliceColor(misc: { slice_color?: string } | undefined): string {
  const hex = sliceColor(misc);
  const n = Number.parseInt(hex.slice(1), 16);
  if (hex.length !== 7 || Number.isNaN(n)) return "#e2e2e6";
  const mix = (c: number, base: number) => Math.round(c * 0.3 + base * 0.7);
  const r = mix((n >> 16) & 0xff, 0xe9);
  const g = mix((n >> 8) & 0xff, 0xe9);
  const b = mix(n & 0xff, 0xeb);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/** The first palette color no other slicer uses yet; cycles once all are taken. */
export function nextSliceColor(existingColors: string[]): string {
  const taken = new Set(existingColors.map((c) => c.toLowerCase()));
  return (
    SLICE_COLOR_PALETTE.find((c) => !taken.has(c)) ??
    SLICE_COLOR_PALETTE[existingColors.length % SLICE_COLOR_PALETTE.length]
  );
}

export function controllerRuntimeId(uuid: string): string {
  return `${CONTROLLER_TYPE}-${uuid}`;
}

export function controllerBareId(runtimeId: string): string {
  return runtimeId.startsWith(`${CONTROLLER_TYPE}-`)
    ? runtimeId.slice(CONTROLLER_TYPE.length + 1)
    : runtimeId;
}

/**
 * Which filter sections each widget type may bind to a controller. Widget definitions
 * mirror this on their `dynamic.bindableSections` field (the forms read it from there);
 * `bindableMiscs` below enumerates the matching builder-misc objects — or, for player_card,
 * the spec itself, since it keeps no builder misc.
 */
export const BINDABLE_SECTIONS = {
  chart: ["date", "subject"],
  pie_chart: ["date", "subject"],
  table: ["date", "subject"],
  table_2d: ["date", "subject"],
  stat_tile: ["date", "subject"],
  gauge: ["date", "subject"],
  // Radar's subjects are per-series vertices, not one shared picker — date only.
  radar: ["date"],
  player_notes: ["date", "subject"],
  // A body map reads one day and one athlete — never the range or the multi-subject picker.
  body_map: ["single_date", "single_player"],
  // A player card holds a bare player_id, so only the single-athlete picker can drive it.
  player_card: ["single_player"],
} as const satisfies Partial<Record<GridWidgetType, readonly ControllerKind[]>>;

/** Whether a widget type's date or subject section may follow a controller. */
export function isSectionBindable(type: GridWidgetType, kind: ControllerKind): boolean {
  const sections = BINDABLE_SECTIONS[type as keyof typeof BINDABLE_SECTIONS] as
    | readonly ControllerKind[]
    | undefined;
  return sections?.includes(kind) ?? false;
}

/**
 * Every builder-misc object on this spec that can hold a binding — one per widget for
 * widget-level filters, one per series for chart/pie. Mutating a returned object mutates
 * the spec.
 */
export function bindableMiscs(spec: ComponentSpec | undefined): ControllerBindingFields[] {
  if (!spec) return [];
  switch (spec.type) {
    case "chart":
    case "pie_chart":
      return spec.series.map((s) => s.series_builder_misc).filter(Boolean);
    case "table":
      return spec.table_builder_misc ? [spec.table_builder_misc] : [];
    case "table_2d":
      return spec.table_2d_builder_misc ? [spec.table_2d_builder_misc] : [];
    case "stat_tile":
      return spec.stat_tile_builder_misc ? [spec.stat_tile_builder_misc] : [];
    case "gauge":
      return spec.gauge_builder_misc ? [spec.gauge_builder_misc] : [];
    case "radar":
      return spec.radar_builder_misc ? [spec.radar_builder_misc] : [];
    case "player_notes":
      return spec.player_notes_builder_misc ? [spec.player_notes_builder_misc] : [];
    case "body_map":
      return spec.body_map_builder_misc ? [spec.body_map_builder_misc] : [];
    // player_card has no builder misc — the binding sits on the spec itself.
    case "player_card":
      return [spec];
    default:
      return [];
  }
}

/** The builder-misc field that stores the binding for a filter section. */
/** The id-bearing binding keys — deliberately NOT `subjectControllerMode`, which is a
 *  modifier on a binding rather than a reference to a controller. */
export type ControllerIdField =
  | "dateControllerId" | "subjectControllerId"
  | "singleDateControllerId" | "singlePlayerControllerId";

const BINDING_FIELD: Record<ControllerKind, ControllerIdField> = {
  date: "dateControllerId",
  subject: "subjectControllerId",
  single_date: "singleDateControllerId",
  single_player: "singlePlayerControllerId",
};

export function bindingField(kind: ControllerKind): ControllerIdField {
  return BINDING_FIELD[kind];
}

// ── Controller lookup ────────────────────────────────────────────────────────

export interface ControllerOption {
  /** Runtime widget id, e.g. `slicer-<uuid>`. */
  id: string;
  /** Bare UUID — what a binding field stores. */
  uuid: string;
  /** Upper-cased slicer key — what a `{Slicer.KEY.Variable}` token quotes. */
  key: string;
  label: string;
  controls: ControllerKind[];
  spec: DynamicControllerComponentSpec;
}

interface WidgetLike {
  id: string;
  type: string;
  spec?: ComponentSpec;
}

/** The saved key and label `assignKeys` derives from, for one slicer spec. */
export function slicerKeySource(
  spec: DynamicControllerComponentSpec,
): { key?: string; label?: string } {
  const misc = spec.slicer_builder_misc as { key?: string; label?: string } | undefined;
  return { key: misc?.key, label: misc?.label };
}

/**
 * Every slicer widget on the dashboard, in widget order. A slicer without a valid saved
 * key gets one derived from its label, in that same order, so every reader agrees.
 */
export function listControllers(widgets: WidgetLike[]): ControllerOption[] {
  const options: ControllerOption[] = [];
  for (const widget of widgets) {
    const spec = widget.spec;
    if (!spec || spec.type !== CONTROLLER_TYPE) continue;
    const uuid = controllerBareId(widget.id);
    options.push({
      id: widget.id,
      uuid,
      key: "",
      label:
        spec.slicer_builder_misc?.label ||
        spec.display?.title ||
        "Slicer",
      controls: spec.controls,
      spec,
    });
  }
  const keys = assignKeys(options.map((o) => slicerKeySource(o.spec)));
  options.forEach((option, i) => { option.key = keys[i]; });
  return options;
}

/** Write a key into a slicer spec's builder misc, in place. */
export function setSlicerKey(spec: DynamicControllerComponentSpec, key: string): void {
  const misc = (spec.slicer_builder_misc ?? {}) as DynamicControllerBuilderMisc & { key?: string };
  misc.key = key;
  spec.slicer_builder_misc = misc;
}

/**
 * Write a derived key into every slicer that has none, in the given order, so the next
 * autosave persists it and a deleted neighbour can never change it later.
 */
export function writeDerivedSlicerKeys(widgets: WidgetLike[]): void {
  const slicers: DynamicControllerComponentSpec[] = [];
  for (const widget of widgets) {
    if (widget.spec?.type === CONTROLLER_TYPE) slicers.push(widget.spec);
  }
  const keys = assignKeys(slicers.map(slicerKeySource));
  slicers.forEach((spec, i) => setSlicerKey(spec, keys[i]));
}

/** Controller key (upper-cased) → bare uuid, for mapping a quoted token to its slicer. */
export function controllerUuidsByKey(controllers: ControllerOption[]): Map<string, string> {
  const byKey = new Map<string, string>();
  for (const c of controllers) if (!byKey.has(c.key)) byKey.set(c.key, c.uuid);
  return byKey;
}

/**
 * The template string a widget renders through `resolveTemplate`: a text widget's content,
 * every other widget's title. Null for a missing spec and for a slicer, whose label is
 * plain text, so a slicer never counts as its own dependent.
 */
export function templateText(spec: ComponentSpec | undefined): string | null {
  if (!spec) return null;
  if (spec.type === CONTROLLER_TYPE) return null;
  if (spec.type === "text") return spec.display?.content ?? "";
  const display = (spec as { display?: { title?: string | null } }).display;
  return display?.title ?? "";
}

/** Controllers that offer a given filter section — the eligible binding targets. */
export function controllersForKind(
  controllers: ControllerOption[],
  kind: ControllerKind,
): ControllerOption[] {
  return controllers.filter((c) => controllerOffersKind(c.controls, kind));
}

/** The uuids a binding may legally point at, for every kind at once. */
export function eligibleByKind(
  controllers: ControllerOption[],
): Record<ControllerKind, Set<string>> {
  return {
    date: controllerIdsForKind(controllers, "date"),
    subject: controllerIdsForKind(controllers, "subject"),
    single_date: controllerIdsForKind(controllers, "single_date"),
    single_player: controllerIdsForKind(controllers, "single_player"),
  };
}

/** The uuids that a binding of this kind may legally point at. */
export function controllerIdsForKind(
  controllers: ControllerOption[],
  kind: ControllerKind,
): Set<string> {
  return new Set(controllersForKind(controllers, kind).map((c) => c.uuid));
}

/**
 * The controller a section actually follows, or undefined. A binding is effective only
 * when it names a controller that still exists AND that controls the matching kind — a
 * stale pointer reads as unset everywhere.
 */
export function effectiveBinding(
  misc: ControllerBindingFields | undefined,
  kind: ControllerKind,
  eligibleIds: Set<string>,
): string | undefined {
  const uuid = misc?.[bindingField(kind)];
  return uuid && eligibleIds.has(uuid) ? uuid : undefined;
}

// ── Values ───────────────────────────────────────────────────────────────────

/**
 * The controller a section follows, or undefined when the binding is unset or stale.
 * A bound section renders THIS controller's values in place of its own dormant ones, so
 * the coach reads what the widget will actually show.
 */
export function boundController(
  controllerId: string | undefined,
  kind: ControllerKind,
  controllers: ControllerOption[],
): ControllerOption | undefined {
  if (!controllerId) return undefined;
  return controllers.find(
    (c) => c.uuid === controllerId && controllerOffersKind(c.controls, kind),
  );
}

/**
 * Human-readable names for a set of governed widgets, for the guard dialogs that list what
 * still follows a controller. A text widget reads "Text widget"; every other widget shows
 * its rendered title, with the tokens resolved, and falls back to its type when it has none.
 */
export function widgetLabels(
  widgetIds: Iterable<string>,
  widgets: Record<string, { type: string; spec?: ComponentSpec }>,
  slicerValues?: SlicerValues,
): string[] {
  const labels: string[] = [];
  for (const id of widgetIds) {
    const widget = widgets[id];
    if (widget?.spec?.type === "text") {
      labels.push("Text widget");
      continue;
    }
    const display = (widget?.spec as { display?: { title?: string | null } } | undefined)?.display;
    const rendered = resolveTemplate(display?.title ?? "", slicerValues).resolved.trim();
    const fallback = widget?.spec?.type === "title" ? "Title widget" : widget?.type || id;
    labels.push(rendered || fallback);
  }
  return labels;
}

/**
 * Rewrite a spec's controller bindings through an import's id remap. A binding whose
 * controller is not part of the import is cleared — the backend rejects a save that keeps
 * a dangling reference.
 */
export function remapControllerBindings(
  spec: ComponentSpec | undefined,
  bareIdMap: Map<string, string>,
): void {
  for (const misc of bindableMiscs(spec)) {
    for (const kind of CONTROLLER_KINDS) {
      const field = bindingField(kind);
      const current = misc[field];
      if (!current) continue;
      const next = bareIdMap.get(current);
      if (next) {
        misc[field] = next;
      } else {
        delete misc[field];
        // The mode is meaningless without the binding it modifies.
        if (kind === "subject") delete misc.subjectControllerMode;
      }
    }
  }
}

// ── Display resolution ───────────────────────────────────────────────────────

/** Builder-misc keys that feed a date label. */
const DATE_DISPLAY_KEYS = [
  "dateRange", "showAdvancedDate",
  // startDateLatestN travels with startDateType: without it a follower reads the
  // controller's "latest_n" and its own (absent) count, so the label says 5 while the
  // backend serves the slicer's.
  "startDateType", "startDateAbsolute", "startDateOffset", "startDateLatestN",
  "endDateType", "endDateAbsolute", "endDateOffset",
  "dayFilter",
] as const;

/** Builder-misc keys that feed a subject label. */
const SUBJECT_DISPLAY_KEYS = [
  "selectedSubjects", "selectedDrills", "subjectActiveTab", "selectedSubjectNames",
] as const;

/**
 * The dropdown offers each subject-capable controller twice: as itself, and as the
 * positions of its athletes. The Select needs one value per option, so the two stored
 * fields (`subjectControllerId` + `subjectControllerMode`) collapse into a composite here.
 */
export const POSITIONS_MODE_SUFFIX = "::positions";

export function bindingOptionValue(uuid: string, mode?: "positions"): string {
  return mode === "positions" ? `${uuid}${POSITIONS_MODE_SUFFIX}` : uuid;
}

export function parseBindingOption(value?: string): {
  uuid?: string;
  mode?: "positions";
} {
  if (!value) return {};
  return value.endsWith(POSITIONS_MODE_SUFFIX)
    ? { uuid: value.slice(0, -POSITIONS_MODE_SUFFIX.length), mode: "positions" as const }
    : { uuid: value };
}

/** Turns subject/drill ids into display names. Supplied by the caller (Redux-backed). */
export type SubjectNameResolver = (selectedSubjects: string[], selectedDrills: string[]) => string[];

/**
 * Mirror of the engine's `_positions_of_subjects`: swap `player-<id>` entries for the
 * `pos-<id>` they play, pass everything else through, dedupe.
 *
 * Display only — the engine does the authoritative conversion by id. Here the join runs
 * through the position NAME, because `getSubjectEntities("player")` exposes a player's
 * position as `detail` (a name) rather than an id. A player whose position does not match
 * a known position entity is dropped, exactly as the backend drops one with no position.
 */
export function positionSubjectsForPlayers(
  selectedSubjects: string[],
  getSubjectEntities: (subjectType: string) => SubjectEntity[],
): string[] {
  const players = getSubjectEntities("player");
  const positions = getSubjectEntities("position");
  const out: string[] = [];
  const seen = new Set<string>();

  for (const subject of selectedSubjects) {
    const match = /^player-(.+)$/.exec(subject);
    if (!match) {
      if (!seen.has(subject)) { seen.add(subject); out.push(subject); }
      continue;
    }
    const positionName = players.find((p) => p.id === match[1])?.detail;
    const position = positionName
      ? positions.find((pos) => pos.name === positionName)
      : undefined;
    if (!position) continue;
    const value = `pos-${position.id}`;
    if (!seen.has(value)) { seen.add(value); out.push(value); }
  }
  return out;
}

/** The same conversion, rendered as names for labels and summaries. */
export function positionNamesForSubjects(
  selectedSubjects: string[],
  getSubjectEntities: (subjectType: string) => SubjectEntity[],
): string[] {
  const positions = getSubjectEntities("position");
  return positionSubjectsForPlayers(selectedSubjects, getSubjectEntities).map((value) => {
    const match = /^pos-(.+)$/.exec(value);
    const name = match ? positions.find((p) => p.id === match[1])?.name : undefined;
    return name ?? value;
  });
}

/**
 * A render-only copy of the spec with every bound section's display fields replaced by its
 * controller's.
 *
 * Series legends, pie slice names and the info popovers all format themselves from
 * `*_builder_misc` — so without this a governed widget plots the controller's data under
 * its own dormant label ("… | Full Team" while the bars show one athlete). The engine does
 * the equivalent substitution for the DATA; this is the same substitution for the LABEL.
 *
 * The saved spec is untouched: queries are still built from `state.widgets`, so the dormant
 * values survive as the restore state.
 */
export function resolveControllerDisplay<T extends ComponentSpec>(
  spec: T,
  controllers: ControllerOption[],
  resolveNames?: SubjectNameResolver,
  getSubjectEntities?: (subjectType: string) => SubjectEntity[],
): T {
  if (controllers.length === 0) return spec;
  const eligible = eligibleByKind(controllers);
  const anyBound = bindableMiscs(spec).some((misc) =>
    CONTROLLER_KINDS.some((kind) => effectiveBinding(misc, kind, eligible[kind])),
  );
  if (!anyBound) return spec;

  const miscOf = (uuid: string): Record<string, unknown> =>
    (controllers.find((c) => c.uuid === uuid)?.spec.slicer_builder_misc ??
      {}) as Record<string, unknown>;

  const copy = JSON.parse(JSON.stringify(spec)) as T;
  for (const misc of bindableMiscs(copy)) {
    const target = misc as unknown as Record<string, unknown>;

    const dateUuid = effectiveBinding(misc, "date", eligible.date);
    // The engine declines a session window for these types, so they query their OWN
    // dates. Copying the slicer's would label the widget with a window it is not using.
    const declinesWindow =
      dateUuid != null
      && declinesSessionWindow(copy.type) != null
      && usesSessionWindow(miscOf(dateUuid));
    if (dateUuid && !declinesWindow) {
      const src = miscOf(dateUuid);
      for (const key of DATE_DISPLAY_KEYS) target[key] = src[key];
      // Chart/pie series miscs read the preset under `dateRangePreset`, widget-level
      // miscs under `dateRange`. Write both so every label formatter agrees.
      target.dateRangePreset = src.dateRange;
    }

    const singleDateUuid = effectiveBinding(misc, "single_date", eligible.single_date);
    if (singleDateUuid) {
      const controller = controllers.find((c) => c.uuid === singleDateUuid);
      const day = controllerSingleDate(
        controller?.controls, controller?.spec.slicer_builder_misc,
      );
      target.dateMode = day.dateMode;
      target.dateOffset = day.dateOffset;
      target.fixedDate = day.fixedDate;
    }

    const singlePlayerUuid = effectiveBinding(misc, "single_player", eligible.single_player);
    if (singlePlayerUuid) {
      const controller = controllers.find((c) => c.uuid === singlePlayerUuid);
      target.selectedPlayer = controller
        ? controllerAthlete(controller.controls, controller.spec.slicer_builder_misc)
        : "";
    }

    const subjectUuid = effectiveBinding(misc, "subject", eligible.subject);
    if (subjectUuid) {
      const src = miscOf(subjectUuid);
      for (const key of SUBJECT_DISPLAY_KEYS) target[key] = src[key];
      // `selectedSubjectNames` is a denormalized cache of the ids beside it, and labels are
      // formatted from it — re-derive it so a stale cache can never surface as a wrong
      // legend. In positions mode the label must name the POSITIONS those athletes play,
      // which is the whole point of the mode.
      const subjects = (src.selectedSubjects as string[]) ?? [];
      const drills = (src.selectedDrills as string[]) ?? [];
      if (target.subjectControllerMode === "positions") {
        if (getSubjectEntities) {
          target.selectedSubjectNames = positionNamesForSubjects(subjects, getSubjectEntities);
        }
      } else if (resolveNames) {
        target.selectedSubjectNames = resolveNames(subjects, drills);
      }
    }
  }
  return copy;
}
